import { HttpError, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useCallback, useEffect, useState } from "react";

import { redirectToWizardChallenge } from "@/lib/challenge";
import { HubMethods } from "@/lib/hubMethods";
import {
  BridgeActivityDirection,
  type BridgeActivityEntry,
  type BridgeActivitySnapshot,
  type BridgeActivityStatistics,
} from "@/types/bridgeActivity";

export type BridgeHubConnectionState = "connecting" | "connected" | "reconnecting" | "disconnected";

function mergeStats(prev: BridgeActivityStatistics | null, entry: BridgeActivityEntry): BridgeActivityStatistics {
  const base = prev ?? { total: 0, zitadelToBus: 0, busToZitadel: 0, byMessageType: {} };
  const byMessageType = { ...base.byMessageType };
  const mt = entry.messageType;
  byMessageType[mt] = (byMessageType[mt] ?? 0) + 1;
  return {
    total: base.total + 1,
    zitadelToBus: base.zitadelToBus + (entry.direction === BridgeActivityDirection.ZitadelToBus ? 1 : 0),
    busToZitadel: base.busToZitadel + (entry.direction === BridgeActivityDirection.BusToZitadel ? 1 : 0),
    byMessageType,
  };
}

const HUB_PATH = "/hubs/bridge-activity";

export function useBridgeActivityHub() {
  const [connectionState, setConnectionState] = useState<BridgeHubConnectionState>("disconnected");
  const [stats, setStats] = useState<BridgeActivityStatistics | null>(null);
  const [entries, setEntries] = useState<BridgeActivityEntry[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);

  const applySnapshot = useCallback((snapshot: BridgeActivitySnapshot) => {
    setStats(snapshot.statistics);
    setEntries(snapshot.recentEntries);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const connection = new HubConnectionBuilder()
      .withUrl(HUB_PATH, { withCredentials: true })
      .withAutomaticReconnect([0, 2000, 10_000, 30_000])
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(HubMethods.Snapshot, (snapshot: BridgeActivitySnapshot) => {
      applySnapshot(snapshot);
    });

    connection.on(HubMethods.Activity, (entry: BridgeActivityEntry) => {
      setEntries((prev) => {
        const next = [...prev, entry];
        while (next.length > 1000) next.shift();
        return next;
      });
      setStats((s) => mergeStats(s, entry));
    });

    connection.onreconnecting(() => {
      setConnectionState("reconnecting");
    });

    connection.onreconnected(() => {
      setConnectionState("connected");
      setLastError(null);
    });

    connection.onclose((error) => {
      if (cancelled) return;
      setConnectionState("disconnected");
      if (error) {
        setLastError(error.message);
      }
    });

    void (async () => {
      try {
        setLastError(null);
        setConnectionState("connecting");
        await connection.start();
        if (cancelled) {
          await connection.stop();
          return;
        }
        if (connection.state === HubConnectionState.Connected) {
          setConnectionState("connected");
        }
      } catch (e) {
        if (cancelled) return;
        setConnectionState("disconnected");
        if (e instanceof HttpError && (e.statusCode === 401 || e.statusCode === 403)) {
          redirectToWizardChallenge();
          return;
        }
        setLastError(e instanceof Error ? e.message : "Connection failed");
      }
    })();

    return () => {
      cancelled = true;
      void connection.stop();
    };
  }, [applySnapshot]);

  return { connectionState, stats, entries, lastError };
}
